// Will contain types that is received from the external world (API, PubNub, etc)
// And is transformed into the internal types - ./chunk.internal.ts

import type { FetchMessagesResponse, MessageEvent } from 'pubnub'
import type { RActiveConversation, RChunk, RNewConversation } from './api/conversations'
import type { ChunkType } from './chunk.internal'

export type OriginFetchMessages = {
  conversation?: RActiveConversation | RNewConversation
  pbMessage: FetchMessagesResponse['channels'][string][0]
}

export enum RChunkTypes {
  sticker = 'sticker',
}

export type InputChunks = MessageEvent | OriginFetchMessages | RChunk

export enum ChunkOrigin {
  pubnubChatEvent = 'pubnubChatEvent', // Live events from PubNub
  pubnubFetchMessages = 'pubnubFetchMessages', // Historical messages from PubNub
  addNotes = 'addNotes', // When you add a note to a chunk from UI
  apiFetchChunks = 'apiFetch',
}
export interface ChatMessageContent {
  sender: 'agent' | 'customer'
  sender_name: string
  sender_email: string
  message: string
  sender_id: string // agent aom id
  // Stable id for the agent's message, minted once by the console and carried on the
  // published content so the widget's live copy and the stored chunk share it, letting
  // the widget deduplicate by id instead of a heuristic.
  message_id?: string
} // Types of messages that can be sent to the PubNub channel
// The same types we get from pubnub.fetchMessages

export interface ChatMessagePayload {
  type: ChunkType.chatMessage
  isPrivate: boolean
  content: ChatMessageContent
  tmpId?: string
}

export interface FileMessageContent {
  sender: 'customer' | 'agent'
  message: Array<{
    file_name: string
    file_id: string
    file_size: number
    file_type: string
    file_url: string
  }>
  // Optional caption for an image+caption file message.
  caption?: string
}
export interface RFileMessageChunk {
  type: ChunkType.file
  tmpId?: string
  content: FileMessageContent
}

// Add this new interface
export interface FileMessagePayload {
  type: ChunkType.file
  isPrivate: boolean
  content: {
    sender: 'agent' | 'customer'
    sender_name: string
    sender_email: string
    message: FileMessageContent['message']
    sender_id: string
    // Stable id for the agent's file message, used for deduplication (see ChatMessageContent).
    message_id?: string
  }
  tmpId?: string // Add this line to make it consistent with ChatMessagePayload
}

// Common interface for shared properties
interface CommonButtonContent {
  button_type: 'reply' | 'website' | 'call'
  text: string
  call_phone: string
  website_url: string
  call_country: string
}

interface CommonHeader {
  url: string
  media_type: string
  name: string
  size: number
}

interface CommonItemContent {
  buttons: Array<CommonButtonContent>
  header?: CommonHeader
  description: string
  title: string
  id?: string
}
// CHUNK_STEP_1 - define the type
// R stands for raw or external chunk that came from BE
export interface RAiMessageChunk {
  content: {
    attributes: Array<{
      subtext_ai_response: string
      source_title: string
      ai_tag: boolean
      source: string
      action: string
      outcome: string
    }>
    bot_uuid: string
    description: string
  }
  type: ChunkType.aiMessage
  published_timestamp: string
}

// Updated RChatButtonChunk
export interface RChatButtonChunk {
  type: ChunkType.chatButton
  content: {
    message: Array<CommonItemContent>
    sender: string
  }
  publishedTimeStamp: string
}

// Updated RChatMultiProductChunk
export interface RChatMultiProductChunk {
  type: ChunkType.chatMultiProduct
  content: {
    message: Array<{
      items: Array<CommonItemContent>
    }>
    sender: string
  }
  publishedTimeStamp: string
}

// Updated RChatMultiProductReplyChunk
export interface RChatMultiProductReplyChunk {
  type: ChunkType.multiProductReply
  content: {
    message: {
      action: string
      product: CommonItemContent
    }
    sender: string
  }
  publishedTimeStamp: string
}

// Updated RChatMenuButtonChunk
export interface RChatMenuButtonChunk {
  type: ChunkType.chatMenuButton
  content: {
    message: Array<{
      buttons: Array<CommonButtonContent>
      description: string
      title: string
    }>
    sender: string
  }
  publishedTimeStamp: string
}

export interface RSignalChunk {
  attributes: Array<{
    identifier: string
    identifier_type: string
    agent_message: string
    supervisor_message: string
    is_actionable: boolean
    signal_message: string
  }>
  channel: string
  channel_meta: {
    error: string | null
    error_data: unknown | null
    replied_message: unknown | null
    replied_to: unknown | null
    status: unknown | null
    timestamp: string | null
    transfer_type: unknown | null
  }
  contact_uuid: string | null
  customer_contact: string
  description: string
  disposition: unknown | null
  disposition_id: unknown | null
  from: string
  from_user_name: string
  hide_conversation: unknown | null
  is_first_chunk_for_conv: boolean
  is_inbound_chunk: boolean
  message_id: string
  reply_to: unknown | null
  retry_chunk_uuid: string | null
  sender_id: string
  sub_disposition: unknown | null
  sub_disposition_id: unknown | null
  timestamp: string
  to: string
  to_user_name: string
  type: string
  uuid: string
}

export interface RFlowExecutionChunkMeta {
  assigned_agent_email: string | null
  assigned_agent_name: string | null
  component_id: string
  create_task_type: string
  customer_contact: string | null
  customer_dropped: boolean | null
  input_value: unknown | null
  is_error: boolean
  method: string | null
  name: string
  node_uuid: string
  output_state: string
  phlo_name: string
  phlo_uuid: string
  selected_api: string | null
  selected_integration: string | null
  sender_id: string | null
  to_user_name: string | null
  type: string
  url: string | null
}
